//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import axios from 'axios'

import { CompanyProfileActionCTO, CompanyProfileActionEnum, CompanyProfileCTO } from '@shared/CompanyProfileCTO'
import { logOT } from '@shared/loggingShared'
import { EthAddr } from '@shared/SchemaTypes'
import { ethers } from 'ethers'
import { User } from 'oidc-client-ts'

export enum TemplateFormat {
  USDC = 'USDC',
  DateTime = 'DateTime',
  EthAddr = 'EthAddr',
}

export const companyProfilePut = async (
  serverApiBaseUrl: string,
  companyProfile: Partial<CompanyProfileCTO>,
  restAPIKey: string,
  cognitoUser: User
): Promise<CompanyProfileCTO> => {
  if (!companyProfile.uuid) {
    companyProfile.uuid = ethers.uuidV4(ethers.randomBytes(16))
  }

  const accessToken = cognitoUser.access_token
  const config = {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': restAPIKey,
      Authorization: `Bearer ${accessToken}`,
    },
  }
  const r = await axios.put(`${serverApiBaseUrl}/companyProfiles/`, companyProfile, config)
  const data = r.data
  logOT.debug('companyProfilePut', data)
  return data.results
}

export const deleteCompanyProfile = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  cognitoUser: User,
  uuid: string
): Promise<CompanyProfileCTO> => {
  const companyProfileActionCTO = {
    uuid,
    action: CompanyProfileActionEnum.DELETE_PROFILE,
  }

  const config = {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': restAPIKey,
      Authorization: `Bearer ${cognitoUser.access_token}`,
    },
  }
  try {
    const r = await axios.post(`${serverApiBaseUrl}/companyProfileAction`, companyProfileActionCTO, config)
    const data = r.data
    logOT.info(deleteCompanyProfile.name, { data, companyProfileActionCTO })
    return data.results
  } catch (error) {
    logOT.error(deleteCompanyProfile.name, 'error', { error })
    throw error
  }
}

export const companyProfileRequestAccess = async (
  serverApiBaseUrl: string,

  restAPIKey: string,
  signedMessage: string,
  uuid: string,
  accountAddr: EthAddr,
  poolAddr: EthAddr
): Promise<CompanyProfileCTO> => {
  logOT.info(companyProfileRequestAccess.name, 'start', {
    signedMessage,
    accountAddr,
    restAPIKey,
  })
  const companyProfileActionCTO: CompanyProfileActionCTO = {
    uuid,
    action: CompanyProfileActionEnum.REQUEST_ACCESS,
    wallets: [accountAddr],
    requestedPoolsAccess: [poolAddr],
  }
  return companyProfileSignedAction(serverApiBaseUrl, companyProfileActionCTO, restAPIKey, signedMessage, accountAddr)
}
export const companyProfileSignedAction = async (
  serverApiBaseUrl: string,
  companyProfileActionCTO: CompanyProfileActionCTO,
  restAPIKey: string,
  signedMessage: string,
  accountAddr: EthAddr
): Promise<CompanyProfileCTO> => {
  if (!companyProfileActionCTO.uuid) {
    companyProfileActionCTO.uuid = ethers.uuidV4(ethers.randomBytes(16))
  }
  logOT.info('companyProfileSignedAction', {
    serverApiBaseUrl,
    companyProfileActionCTO,
    signedMessage,
    accountAddr,
    restAPIKey,
  })
  const config = {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': restAPIKey,
      Authorization: signedMessage,
      //      'x-public-key-id': accountAddr,
    },
  }
  logOT.info(companyProfileSignedAction.name, 'start', {
    config,
    companyProfileActionCTO,
  })
  try {
    const r = await axios.post(`${serverApiBaseUrl}/companyProfileSignedAction`, companyProfileActionCTO, config)
    const data = r.data
    logOT.info(companyProfileSignedAction.name, { data, companyProfileActionCTO })
    return data.results
  } catch (error) {
    logOT.error(companyProfileSignedAction.name, 'error', { error })
    throw error
  }
}

export const fetchCompanyProfiles = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  cognitoUser: User,
  skipAccessControls?: boolean
): Promise<CompanyProfileCTO[]> => {
  logOT.info(fetchCompanyProfiles.name, 'start', {
    serverApiBaseUrl,
    restAPIKey,
    cognitoUser: cognitoUser.access_token,
  })
  const r = await axios.get(
    `${serverApiBaseUrl}/companyProfiles${skipAccessControls ? '?skipAccessControls=true' : ''}`,
    {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': restAPIKey,
        Authorization: `Bearer ${cognitoUser.access_token}`,
      },
    }
  )

  const data = r.data
  logOT.info(fetchCompanyProfiles.name, { data })
  const profiles = data.results
  return profiles
}

export const fetchCompanyProfileSigned = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  walletAddr: EthAddr,

  signedMessage: string
): Promise<CompanyProfileCTO | undefined> => {
  logOT.info(fetchCompanyProfileSigned.name, 'start', {
    serverApiBaseUrl,
    restAPIKey,
    walletAddr,
  })
  const r = await axios.get(`${serverApiBaseUrl}/companyProfileSigned?walletAddr=${walletAddr}`, {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': restAPIKey,
      Authorization: signedMessage,
    },
  })

  const data = r.data
  logOT.info(fetchCompanyProfileSigned.name, { data })

  return data.companyProfile
}

export const toCompanyAddrString = (
  addr: EthAddr,
  companyProfiles: CompanyProfileCTO[]
): { display: string; companyProfile: CompanyProfileCTO | undefined } => {
  const companyProfile = companyProfiles.find((profile) => profile.wallets.includes(addr))
  if (companyProfile) {
    return { display: companyProfile.companyName + ' ' + addr, companyProfile: companyProfile }
  }
  return { display: addr, companyProfile: undefined }
}

export const toCompanyName = (addr: EthAddr, companyProfiles: CompanyProfileCTO[]): string => {
  const companyProfile = companyProfiles.find((profile) => profile.wallets.includes(addr))
  if (companyProfile) {
    return companyProfile.companyName
  }
  return '-'
}

export const fetchCompanyProfileExists = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  walletAddrOrId: EthAddr | string
): Promise<CompanyProfileCTO | undefined> => {
  logOT.info(fetchCompanyProfileExists.name, 'start', {
    serverApiBaseUrl,
    restAPIKey,
    walletAddrOrId,
  })
  const r = await axios.get(`${serverApiBaseUrl}/companyProfileExists/${walletAddrOrId}`, {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': restAPIKey,
    },
  })

  const data = r.data
  logOT.info(fetchCompanyProfileExists.name, { data })

  return data.exists
}
